import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import './AdminConsolePage.css';

interface User {
  email: string;
  name: string;
  role: string;
  is_approved: boolean;
  is_active: boolean;
  created_at: string;
  approved_at?: string;
  approved_by?: string;
}

const AVAILABLE_ROLES = ['Admin', 'QA_Lead', 'QA_Engineer', 'Viewer'];

export const AdminConsolePage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [activeTab, setActiveTab] = useState<'pending' | 'all' | 'roles'>('pending');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('Viewer');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadUsers();
  }, [activeTab]);

  const loadUsers = async () => {
    setIsLoading(true);
    setError('');
    try {
      if (activeTab === 'pending') {
        const users = await apiClient.getPendingUsers();
        setPendingUsers(users);
      } else {
        const users = await apiClient.getAllUsers();
        setAllUsers(users);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (userEmail: string) => {
    try {
      setError('');
      setSuccessMessage('');
      await apiClient.approveUser(userEmail);
      setSuccessMessage('User approved successfully');
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to approve user');
    }
  };

  const handleReject = async (userEmail: string) => {
    if (!window.confirm('Are you sure you want to reject this user? This action cannot be undone.')) {
      return;
    }
    try {
      setError('');
      setSuccessMessage('');
      await apiClient.rejectUser(userEmail);
      setSuccessMessage('User rejected successfully');
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to reject user');
    }
  };

  const handleDeactivate = async (userEmail: string) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) {
      return;
    }
    try {
      setError('');
      setSuccessMessage('');
      await apiClient.deactivateUser(userEmail);
      setSuccessMessage('User deactivated successfully');
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate user');
    }
  };

  const handleActivate = async (userEmail: string) => {
    try {
      setError('');
      setSuccessMessage('');
      await apiClient.activateUser(userEmail);
      setSuccessMessage('User activated successfully');
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to activate user');
    }
  };

  const handleChangeRole = async (userEmail: string, newRole: string) => {
    try {
      setError('');
      setSuccessMessage('');
      await apiClient.updateUserRole(userEmail, newRole);
      setSuccessMessage('Role updated successfully');
      setEditingRole(null);
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to update role');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (currentUser?.role !== 'Admin') {
    return (
      <div className="admin-console-page">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access the admin console.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-console-page">
      <div className="admin-header">
        <h1>Admin Console</h1>
        <p>Manage user accounts and permissions</p>
      </div>

      <div className="admin-tabs">
        <button
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          Pending Approvals ({pendingUsers.length})
        </button>
        <button
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All Users ({allUsers.length})
        </button>
        <button
          className={`tab ${activeTab === 'roles' ? 'active' : ''}`}
          onClick={() => setActiveTab('roles')}
        >
          Role Management
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {isLoading ? (
        <div className="loading">Loading users...</div>
      ) : (
        <div className="users-container">
          {activeTab === 'pending' && (
            <div className="pending-users">
              {pendingUsers.length === 0 ? (
                <div className="empty-state">
                  <p>No pending user approvals</p>
                </div>
              ) : (
                <div className="users-list">
                  {pendingUsers.map((user) => (
                    <div key={user.email} className="user-card pending">
                      <div className="user-info">
                        <h3>{user.name}</h3>
                        <p className="user-email">{user.email}</p>
                        <p className="user-meta">
                          Registered: {formatDate(user.created_at)}
                        </p>
                      </div>
                      <div className="user-actions">
                        <button
                          className="btn btn-success"
                          onClick={() => handleApprove(user.email)}
                        >
                          Approve
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={() => handleReject(user.email)}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'all' && (
            <div className="all-users">
              {allUsers.length === 0 ? (
                <div className="empty-state">
                  <p>No users found</p>
                </div>
              ) : (
                <div className="users-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Registered</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allUsers.map((user) => (
                        <tr key={user.email}>
                          <td>{user.name}</td>
                          <td>{user.email}</td>
                          <td>
                            {editingRole === user.email ? (
                              <div className="role-edit">
                                <select
                                  value={selectedRole}
                                  onChange={(e) => setSelectedRole(e.target.value)}
                                  className="role-select"
                                >
                                  {AVAILABLE_ROLES.map(role => (
                                    <option key={role} value={role}>{role}</option>
                                  ))}
                                </select>
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() => handleChangeRole(user.email, selectedRole)}
                                >
                                  Save
                                </button>
                                <button
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => setEditingRole(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="role-display">
                                <span className={`role-badge ${user.role}`}>
                                  {user.role}
                                </span>
                                {user.email !== currentUser?.email && (
                                  <button
                                    className="btn-icon"
                                    onClick={() => {
                                      setEditingRole(user.email);
                                      setSelectedRole(user.role);
                                    }}
                                    title="Change role"
                                  >
                                    ✏️
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                          <td>
                            <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </span>
                            {!user.is_approved && (
                              <span className="status-badge pending">Pending</span>
                            )}
                          </td>
                          <td>{formatDate(user.created_at)}</td>
                          <td>
                            {user.email !== currentUser?.email && (
                              <div className="action-buttons">
                                {user.is_active ? (
                                  <button
                                    className="btn btn-sm btn-warning"
                                    onClick={() => handleDeactivate(user.email)}
                                  >
                                    Deactivate
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-sm btn-success"
                                    onClick={() => handleActivate(user.email)}
                                  >
                                    Activate
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'roles' && (
            <div className="role-management">
              <div className="roles-info">
                <h3>Available Roles</h3>
                <div className="roles-grid">
                  {AVAILABLE_ROLES.map(role => (
                    <div key={role} className="role-card">
                      <h4>{role}</h4>
                      <p className="role-description">
                        {role === 'Admin' && 'Full system access including user management'}
                        {role === 'QA_Lead' && 'Can manage projects, scenarios, and test cases'}
                        {role === 'QA_Engineer' && 'Can create and execute tests'}
                        {role === 'Viewer' && 'Read-only access to view tests and results'}
                      </p>
                      <div className="role-permissions">
                        <strong>Permissions:</strong>
                        <ul>
                          {role === 'Admin' && (
                            <>
                              <li>✓ Manage users and roles</li>
                              <li>✓ Create/edit/delete projects</li>
                              <li>✓ Create/edit/delete scenarios</li>
                              <li>✓ Create/edit/delete test cases</li>
                              <li>✓ Execute tests</li>
                              <li>✓ Export and import</li>
                            </>
                          )}
                          {role === 'QA_Lead' && (
                            <>
                              <li>✓ Create/edit/delete projects</li>
                              <li>✓ Create/edit/delete scenarios</li>
                              <li>✓ Create/edit/delete test cases</li>
                              <li>✓ Execute tests</li>
                              <li>✓ Export test cases</li>
                            </>
                          )}
                          {role === 'QA_Engineer' && (
                            <>
                              <li>✓ Create/edit/delete scenarios</li>
                              <li>✓ Create/edit/delete test cases</li>
                              <li>✓ Execute tests</li>
                              <li>✓ Export and import</li>
                              <li>✓ View projects</li>
                            </>
                          )}
                          {role === 'Viewer' && (
                            <>
                              <li>✓ View projects</li>
                              <li>✓ View scenarios</li>
                              <li>✓ View test cases</li>
                              <li>✓ View test results</li>
                            </>
                          )}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="permission-info">
                <h3>Permission Modules</h3>
                <div className="modules-list">
                  <div className="module-item">
                    <strong>Projects:</strong> Create, read, update, delete projects
                  </div>
                  <div className="module-item">
                    <strong>Test Scenarios:</strong> Create, read, update, delete scenarios
                  </div>
                  <div className="module-item">
                    <strong>Test Cases:</strong> Create, read, update, delete test cases
                  </div>
                  <div className="module-item">
                    <strong>Test Execution:</strong> Execute tests and record results
                  </div>
                  <div className="module-item">
                    <strong>User Management:</strong> Manage users, roles, and permissions
                  </div>
                  <div className="module-item">
                    <strong>Export/Import:</strong> Export test cases and import data
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
